import React, {FC, useCallback, useRef, useState} from 'react';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {usePushEvent} from '../../../srcCommon/push/pushContext';
import {PushEventTypesEnum} from '../../../srcCommon/push/pushHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {ModelType} from '../../models/api/apiTypes';
import {getUserId} from '../../store/data/teammates/teammatesSelectors';
import {ApiTagsBatchData} from '../tags/settingsTagsListTypes';
import {SettingsBannerContext, SettingsBannerDefinition} from './settingsBannersContext';

/*
 * Props.
 */

interface SettingsBannersProviderProps {}

/*
 * Component.
 */

export const SettingsBannersProvider: FC<SettingsBannersProviderProps> = (props) => {
  const [banner, setBanner] = useState<SettingsBannerDefinition | undefined>();
  const [bannerPatch, setBannerPatch] = useState<SettingsBannerDefinition | undefined>();
  const timeoutId = useRef(0);
  const userId = useAppSelector(getUserId);

  const resetState = useCallback(() => {
    setBanner(undefined);
    setBannerPatch(undefined);
  }, []);

  const onTagsBatchEvent = useCallback(
    (data: ModelType<typeof ApiTagsBatchData['runtimeType']>) => {
      if (data.requesterId !== userId) {
        return;
      }

      if (!banner) {
        return;
      }

      clearTimeout(timeoutId.current);
      setBanner(bannerPatch);

      timeoutId.current = globalWindow.setInstrumentedTimeout(resetState, bannerPatch?.dismissAfter ?? 5000);
    },
    [banner, bannerPatch, resetState, userId],
  );

  const setBannerPatchForTagsBulk: HandlerOf<SettingsBannerDefinition> = useCallback((newBannerPatch) => {
    setBannerPatch(newBannerPatch);
  }, []);

  usePushEvent(PushEventTypesEnum.TAGS_UPDATED_BATCH, ApiTagsBatchData, onTagsBatchEvent);
  usePushEvent(PushEventTypesEnum.TAGS_DELETED_BATCH, ApiTagsBatchData, onTagsBatchEvent);

  const onShowBanner: HandlerOf<SettingsBannerDefinition> = useCallback(
    (bannerDefinition) => {
      clearTimeout(timeoutId.current);

      setBanner(bannerDefinition);

      timeoutId.current = globalWindow.setInstrumentedTimeout(
        resetState,
        bannerDefinition.dismissAfter ?? 5000,
      );
    },
    [resetState],
  );

  return (
    <SettingsBannerContext.Provider
      value={{
        showBanner: onShowBanner,
        updateBannerOnTagsBulk: setBannerPatchForTagsBulk,
        currentBanner: banner,
        dismissBanner: () => setBanner(undefined),
      }}
    >
      {props.children}
    </SettingsBannerContext.Provider>
  );
};
